import React, { useState } from "react";
import { LogIn, Phone, Lock, Heart, Eye, EyeOff, User, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { useOtpGate } from "./OtpGate";
import { ForgotPasswordDialog } from "./ForgotPasswordDialog";
import { useCategories } from "../context/CategoriesContext";
import { CITIES } from "../mock/mockData";
import { districtsOf } from "../lib/districts";
import { formatPhone } from "../lib/phone";

const FirmaLoginDialog = ({ open, onOpenChange }) => {
  const { categories: CATEGORIES } = useCategories();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [showPass, setShowPass] = useState(false);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [category, setCategory] = useState("dugun-salonlari");
  const [city, setCity] = useState("Ankara");
  const [district, setDistrict] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const { gate, dialog: otpDialog } = useOtpGate("firm");
  const [forgotOpen, setForgotOpen] = useState(false);
  const reset = () => {
    setPhone(""); setPassword(""); setName(""); setContactName("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone || !password || (mode === "register" && (!name || !contactName || !district))) {
      toast({ title: "Eksik bilgi", description: "Lütfen zorunlu alanları doldurun." });
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(phone, password);
        toast({ title: "Giriş başarılı", description: "Firma panelinize yönlendiriliyorsunuz." });
      } else {
        await gate(phone, async (otp) => {
          await register({ name, contactName, phone, password, category, city, district, otp });
          toast({ title: "Kayıt başarılı", description: "Hoş geldiniz! Panelinizi doldurabilirsiniz." });
          onOpenChange(false);
          reset();
          navigate("/firma-panel");
        });
        return;
      }
      onOpenChange(false);
      reset();
      navigate("/firma-panel");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Bir hata oluştu, tekrar deneyin.";
      toast({ title: "Hata", description: msg });
    } finally {
      setLoading(false);
    }
  };

  const isLogin = mode === "login";

  return (
    <>
    {otpDialog}
    <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} purpose="firm" initialPhone={phone} />
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#FBF5F0] border-[#F0DDE0] p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        <div className="bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] px-6 py-6 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-white/15 flex items-center justify-center mb-2">
            <Heart className="w-6 h-6 text-white" fill="white" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-white font-display text-2xl text-center">
              {isLogin ? "Firma Girişi" : "Firma Kaydı"}
            </DialogTitle>
            <DialogDescription className="text-white/85 text-center">
              {isLogin ? "Telefon ve şifrenizle giriş yapın" : "Hemen ücretsiz hesabınızı oluşturun"}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* mode toggle */}
        <div className="flex mx-6 mt-5 rounded-xl bg-[#F1DEDF] p-1">
          <button
            type="button"
            onClick={() => setMode("login")}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${isLogin ? "bg-white text-[#9E2A3B] shadow-sm" : "text-[#8a6d70]"}`}
          >
            Giriş Yap
          </button>
          <button
            type="button"
            onClick={() => setMode("register")}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${!isLogin ? "bg-white text-[#9E2A3B] shadow-sm" : "text-[#8a6d70]"}`}
          >
            Kayıt Ol
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {!isLogin && (
            <div className="space-y-1.5">
              <Label className="text-[#2A2224]">Firma / Salon Adı</Label>
              <div className="relative">
                <User className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn. Elit Balo Salonu" className="pl-9 bg-white border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              </div>
            </div>
          )}

          {!isLogin && (
            <div className="space-y-1.5">
              <Label className="text-[#2A2224]">Ad Soyad</Label>
              <div className="relative">
                <User className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="dialog-contactname-input" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Yetkili adı soyadı" className="pl-9 bg-white border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-[#2A2224]">Telefon</Label>
            <div className="relative">
              <Phone className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input data-testid="firm-dialog-phone" value={phone} onChange={(e) => setPhone(e.target.value)} onBlur={() => setPhone(formatPhone(phone))} inputMode="tel" placeholder="05xx xxx xx xx" className="pl-9 bg-white border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[#2A2224]">Şifre</Label>
            <div className="relative">
              <Lock className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-9 pr-10 bg-white border-[#F0DDE0] focus-visible:ring-[#C15B6B]"
              />
              <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7C6E70] hover:text-[#9E2A3B]" aria-label="Şifreyi göster">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {isLogin && (
              <button type="button" onClick={() => setForgotOpen(true)} data-testid="firm-forgot-link" className="text-xs text-[#9E2A3B] hover:underline">Şifremi unuttum</button>
            )}
          </div>

          {!isLogin && (
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[#2A2224]">Kategori</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="bg-white border-[#F0DDE0] focus:ring-[#C15B6B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {CATEGORIES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#2A2224]">Şehir</Label>
                <Select value={city} onValueChange={(v) => { setCity(v); setDistrict(""); }}>
                  <SelectTrigger data-testid="firm-register-city" className="bg-white border-[#F0DDE0] focus:ring-[#C15B6B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#2A2224]">İlçe</Label>
                <Select value={district} onValueChange={setDistrict}>
                  <SelectTrigger data-testid="firm-register-district" className="bg-white border-[#F0DDE0] focus:ring-[#C15B6B]"><SelectValue placeholder="İlçe seçin" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {districtsOf(city).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-70"
          >
            {isLogin ? <LogIn className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
            {loading ? "Lütfen bekleyin..." : isLogin ? "Giriş Yap" : "Kayıt Ol"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default FirmaLoginDialog;
